import type { ReactNode } from "react";

import TikTokIcon from "@/assets/tiktok";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { TikTokFeatureKey } from "@/services/tiktokShop.service";

/**
 * PRE_RELEASE_TIKTOK — the badges and preview panels pilot brands see while
 * TikTok reviews Reveshare's affiliate scopes. Delete this file at GA.
 */

export const BETA_HINT =
  "You're seeing TikTok Shop analytics early. Creator tools unlock as TikTok approves them.";
export const COMING_SOON_HINT =
  "Waiting on TikTok's approval of Reveshare's creator permissions.";

interface BadgeProps {
  className?: string;
  /**
   * Pin the badge to the top-right corner of the nearest `relative` parent
   * instead of flowing inline. `className` carries the offsets (e.g.
   * `-top-2 -right-2`); the default hangs just outside the corner.
   */
  floating?: boolean;
  /** `sm` for rows and chips (sidebar, nav, tabs); `md` for cards and headers. */
  size?: "sm" | "md";
}

const BADGE_SIZE = {
  sm: "px-1.5 py-0 text-[9px] leading-4",
  md: "px-2 py-0.5 text-[11px]",
} as const;

const HAS_OFFSET = /(^|\s)-?(top|right)-/;

/** Shared chrome: inline by default, pinned to the corner when floating. */
function badgeClass({ floating, size = "md", className }: BadgeProps, tone: string) {
  return cn(
    "inline-flex items-center whitespace-nowrap rounded-md border font-semibold uppercase tracking-wider",
    BADGE_SIZE[size],
    tone,
    floating && "pointer-events-none absolute z-10 shadow-sm",
    floating && !HAS_OFFSET.test(className ?? "") && "-right-2 -top-2",
    className,
  );
}

export function BetaBadge(props: BadgeProps) {
  return (
    <span
      title={BETA_HINT}
      // Solid brand fill: the outlined version vanished on dark surfaces.
      className={badgeClass(props, "border-brand-600 bg-brand-600 text-white dark:border-brand-500 dark:bg-brand-500")}
    >
      Beta
    </span>
  );
}

export function ComingSoonBadge(props: BadgeProps) {
  return (
    <span
      title={COMING_SOON_HINT}
      // Inverted: ink on light, paper on dark, so it reads either way.
      className={badgeClass(props, "border-foreground bg-foreground text-background")}
    >
      Coming soon
    </span>
  );
}

interface Preview {
  title: string;
  description: string;
  bullets: [string, string, string];
  action: string;
}

/** What each coming-soon surface will do, in the brand's words. */
export const TIKTOK_PREVIEWS: Record<Exclude<TikTokFeatureKey, "analytics">, Preview> = {
  samples: {
    title: "Sample requests",
    description:
      "Review creators asking for free samples, approve the right ones automatically, and see what each sample turns into.",
    bullets: [
      "Approve or decline with one click, or set rules so Reveshare decides for you",
      "See a creator's followers, sales band and past videos before you ship",
      "Follow each sample from shipped to posted to sold",
    ],
    action: "Review requests",
  },
  adRequests: {
    title: "Ad requests",
    description:
      "Turn a creator's sample video into a paid ad with a flat-fee offer they accept in one tap.",
    bullets: [
      "Offer a flat fee on any creator video",
      "The creator accepts from a link, no account needed",
      "Approved videos become Meta ad drafts automatically",
    ],
    action: "Send an offer",
  },
  invites: {
    title: "Collab invites",
    description:
      "Invite TikTok creators to your affiliate program with a commission offer, straight from search.",
    bullets: [
      "Pick creators from Social Search or your bestsellers list",
      "Set the commission rate and sample policy per invite",
      "Track accepted, pending and expired invites",
    ],
    action: "Invite creators",
  },
  messages: {
    title: "Messages",
    description: "Reply to creators from one inbox instead of Seller Center.",
    bullets: [
      "Every creator conversation in one place",
      "Jump from a message to an invite or a sample in one click",
      "Know when a creator writes back",
    ],
    action: "Open inbox",
  },
  creatorSearch: {
    title: "TikTok creators",
    description: "Search TikTok Shop's creator marketplace and invite the right ones in one flow.",
    bullets: [
      "Filter by category, followers and sales band",
      "See who already sells for brands like yours",
      "Send invites without leaving Reveshare",
    ],
    action: "Search creators",
  },
};

/** A tab body for a surface that waits on TikTok: what it does, nothing it can do yet. */
export function ComingSoonPanel({
  feature,
  icon,
}: {
  feature: Exclude<TikTokFeatureKey, "analytics">;
  icon?: ReactNode;
}) {
  const preview = TIKTOK_PREVIEWS[feature];
  return (
    <div className="mx-auto flex max-w-xl flex-col items-center px-6 py-14 text-center">
      <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-muted">
        {icon ?? <TikTokIcon className="h-7 w-7" />}
      </div>
      <div className="flex items-center gap-2">
        <h2 className="text-lg font-semibold text-foreground">{preview.title}</h2>
        <ComingSoonBadge />
      </div>
      <p className="mt-2 text-sm text-muted-foreground">{preview.description}</p>
      <ul className="mt-5 space-y-2 text-left text-sm text-foreground">
        {preview.bullets.map((line) => (
          <li key={line} className="flex gap-2">
            <span aria-hidden className="mt-2 size-1.5 shrink-0 rounded-full bg-brand-500" />
            <span>{line}</span>
          </li>
        ))}
      </ul>
      <Button className="mt-6" disabled title={COMING_SOON_HINT}>
        {preview.action}
      </Button>
      <p className="mt-3 text-xs text-muted-foreground">{COMING_SOON_HINT}</p>
    </div>
  );
}
